import React, {useContext} from "react";
import {
    AddDriverDetailArgs,
    DrivingDirection,
    EventReportFormPayload,
    RemoveDriverDetailsArgs,
    SetDriverDetailArgs,
    SetDriverDirectionArgs,
    VehicleDetails,
    VehicleDriver
} from "../../../../../../../../store/dutyManagerReport/actions/DutyManagerReportActionTypes";
import {DutyManagerReportEditableContext} from "../../../Context/DutyManagerReportEditable";
import {useDispatch, useSelector} from "react-redux";
import {
    addNewDriverDetails,
    addNewVehicle,
    removeDriverDetails,
    removeVehicleDetails,
    setDriverDetails,
    setDriverDirection,
    setVehicle
} from "../../../../../../../../store/dutyManagerReport/actions/DutyManagerReportActions";
import {FaPlus} from "react-icons/fa";
import {RootStore} from "../../../../../../../../store/Store";
import {AssignmentState, StaffLink, VehicleListResponse} from "../../../../../../../../api/grs";
import {DDProps} from "../../../../../../../Dropdown/Helpers/dropdownUtils";
import Select, {SingleValue} from "react-select";
import {TbTrash} from "react-icons/tb";
import {getTotalAssignments} from "../../../../../../AdminCalendarEntry/Helpers/calenderEntryHelpers";

const VehicleSection = (props: EventReportFormPayload) => {
    const dispatch = useDispatch();
    const editable = useContext(DutyManagerReportEditableContext);
    const entry = useSelector((state: RootStore) => state.calendarEntry.data);
    let allStaff: StaffLink[] = [];

    if (entry) {
        allStaff = getTotalAssignments(entry)
            .filter((x) => x.state !== AssignmentState.Unassigned && x.staffMember !== undefined)
            .map((x) => x.staffMember) as StaffLink[];
        allStaff.sort((a, b) => a.staffName.localeCompare(b.staffName));
    }

    const addVehicleDetails = () => {
        dispatch(addNewVehicle());
    };

    return (
        <>
            <div className="eos-section">
                <div className="eos-vehicle-section">
                    <h2 className="eos-title">
                        Vehicles
                        <button
                            className="add-btn"
                            disabled={!editable}
                            onClick={addVehicleDetails}
                        >
                            <FaPlus /> Add Vehicle
                        </button>
                    </h2>
                    {props.vehicles?.map((vehicle) => (
                        <VehicleRow availableStaff={allStaff} vehicle={vehicle} key={vehicle.uid} />
                    ))}
                </div>
            </div>
        </>
    );
};

export default VehicleSection;

interface VehicleRowProps {
    availableStaff: StaffLink[];
    vehicle: VehicleDetails;
}

function VehicleRow(props: VehicleRowProps) {
    const dispatch = useDispatch();
    const editable = useContext(DutyManagerReportEditableContext);
    const allVehicles = useSelector((state: RootStore) => state.vehicleList.data);
    const vehicleOptions = getVehicleOptions(allVehicles, props.vehicle.vehicleRegistration);
    const selectedOption = vehicleOptions.find(
        (x) => x.value === props.vehicle.vehicleRegistration
    );

    const updateVehicleDetails = (p: SingleValue<DDProps>) => {
        if (!p || !p.value) {
            p = vehicleOptions[0];
        }

        const updatedDetails: VehicleDetails = {
            ...props.vehicle,
            vehicleRegistration: p.value as string
        };

        dispatch(setVehicle(updatedDetails));
    };

    const deleteVehicle = () => {
        dispatch(removeVehicleDetails({uid: props.vehicle.uid}));
    };

    const addDriver = () => {
        const args: AddDriverDetailArgs = {
            vehicleUID: props.vehicle.uid,
            newAssignment: props.availableStaff[0]
        };
        dispatch(addNewDriverDetails(args));
    };

    return (
        <div className="eos-vehicle">
            <h2>Vehicle</h2>
            <div className="eos-vehicle-head">
                <Select
                    maxMenuHeight={200}
                    className="vehicle-name-select"
                    classNamePrefix="select-options"
                    options={vehicleOptions}
                    onChange={updateVehicleDetails}
                    value={selectedOption}
                    noOptionsMessage={() => "No vehicles found"}
                    placeholder="Select Vehicle..."
                    isDisabled={!editable}
                    isClearable={false}
                    isSearchable={true}
                    menuPortalTarget={document.body}
                />

                <div className="controls">
                    {editable && (
                        <button
                            className="eos-icon-btn eos-icon-btn-danger"
                            onClick={deleteVehicle}
                            title="Delete Vehicle"
                        >
                            <TbTrash size={24} />
                        </button>
                    )}
                </div>
            </div>

            <div className="eos-vehicle-drivers">
                <h2>Drivers</h2>
                {props.vehicle.assignments.map((driver: VehicleDriver, index: number) => (
                    <DriverRow
                        availableStaff={props.availableStaff}
                        vehicle={props.vehicle}
                        driver={driver}
                        key={`${props.vehicle.uid}-${index}`}
                    />
                ))}
            </div>

            {editable && (
                <button className="eos-btn add-driver-btn" disabled={!editable} onClick={addDriver}>
                    <FaPlus /> Add Driver
                </button>
            )}
        </div>
    );
}

interface DriverRowProps {
    availableStaff: StaffLink[];
    vehicle: VehicleDetails;
    driver: VehicleDriver;
}

function DriverRow(props: DriverRowProps) {
    const dispatch = useDispatch();
    const editable = useContext(DutyManagerReportEditableContext);
    const driverOptions = getDriverOptions(props.availableStaff, props.driver);
    const selectedOption = driverOptions.find((x) => x.value === props.driver.staffId);
    const outboundChecked =
        props.driver.drivingDirection === DrivingDirection.Outbound ||
        props.driver.drivingDirection === DrivingDirection.Both;
    const inboundChecked =
        props.driver.drivingDirection === DrivingDirection.Inbound ||
        props.driver.drivingDirection === DrivingDirection.Both;

    const deleteDriverDetails = () => {
        const args: RemoveDriverDetailsArgs = {
            uid: props.driver.uid
        };
        dispatch(removeDriverDetails(args));
    };

    const updateDriverDetails = (p: SingleValue<DDProps>) => {
        const staffLink = props.availableStaff.find((x) => x.staffId === p?.value);
        if (!staffLink) return;

        const args: SetDriverDetailArgs = {
            vehicleUID: props.vehicle.uid,
            previousAssignmentUID: props.driver.uid,
            newAssignment: staffLink
        };
        dispatch(setDriverDetails(args));
    };

    const setDrivingDirection = (drivingDirection: DrivingDirection) => {
        const args: SetDriverDirectionArgs = {
            assignmentUID: props.driver.uid,
            newDirection: drivingDirection
        };
        dispatch(setDriverDirection(args));
    };

    const outboundChanged = (checked: boolean) => {
        let newDirection: DrivingDirection = DrivingDirection.Inbound;

        if (!checked) {
            newDirection = DrivingDirection.Inbound;
        } else {
            if (props.driver.drivingDirection == DrivingDirection.Inbound)
                newDirection = DrivingDirection.Both;
            else if (props.driver.drivingDirection == undefined)
                newDirection = DrivingDirection.Outbound;
        }

        setDrivingDirection(newDirection);
    };

    const inboundChanged = (checked: boolean) => {
        let newDirection: DrivingDirection = DrivingDirection.Outbound;

        if (!checked) {
            newDirection = DrivingDirection.Outbound;
        } else {
            if (props.driver.drivingDirection == DrivingDirection.Outbound)
                newDirection = DrivingDirection.Both;
            else if (props.driver.drivingDirection == undefined)
                newDirection = DrivingDirection.Inbound;
        }

        setDrivingDirection(newDirection);
    };

    return (
        <div className="eos-vehicle-driver">
            <div className="eos-vehicle-driver-name">
                <Select
                    maxMenuHeight={200}
                    className="driver-name-select"
                    classNamePrefix="select-options"
                    options={driverOptions}
                    onChange={updateDriverDetails}
                    value={selectedOption}
                    noOptionsMessage={() => "No drivers found"}
                    placeholder="Select Driver..."
                    isDisabled={!editable}
                    isClearable={false}
                    isSearchable={true}
                    menuPortalTarget={document.body}
                />

                <div className="eos-driver-controls">
                    {props.vehicle.assignments.length > 1 && (
                        <button
                            className="eos-icon-btn eos-icon-btn-danger"
                            onClick={deleteDriverDetails}
                            disabled={!editable}
                            title="Remove Driver"
                        >
                            <TbTrash size={24} />
                        </button>
                    )}
                </div>
            </div>

            <div className="eos-driver-direction">
                <label className="eos-driver-checkbox">
                    <input
                        type="checkbox"
                        className="driver-checkbox"
                        disabled={!editable}
                        checked={outboundChecked}
                        onChange={(e) => outboundChanged(e.target.checked)}
                    />
                    Outbound
                </label>

                <label className="eos-driver-checkbox">
                    <input
                        type="checkbox"
                        className="driver-checkbox"
                        disabled={!editable}
                        checked={inboundChecked}
                        onChange={(e) => inboundChanged(e.target.checked)}
                    />
                    Inbound
                </label>
            </div>

            <div className="eos-driver-controls">
                {props.vehicle.assignments.length > 1 && (
                    <button
                        className="eos-icon-btn eos-icon-btn-danger"
                        onClick={deleteDriverDetails}
                        disabled={!editable}
                        title="Remove Driver"
                    >
                        <TbTrash size={24} />
                    </button>
                )}
            </div>
        </div>
    );
}

function getDriverOptions(
    availableStaff: StaffLink[],
    selectedDriver: VehicleDriver | null | undefined
): DDProps[] {
    const options: DDProps[] = [];
    let existingFound = false;

    for (const staff of availableStaff) {
        if (selectedDriver && staff.staffId === selectedDriver.staffId) {
            existingFound = true;
        }

        options.push({
            value: staff.staffId,
            label: staff.staffName
        });
    }

    options.sort((a, b) => a.label.localeCompare(b.label));

    if (!existingFound && selectedDriver) {
        options.unshift({
            value: selectedDriver.staffId,
            label: selectedDriver.staffName
        });
    }

    return options;
}

function getVehicleOptions(
    vehicleResponse: VehicleListResponse | null | undefined,
    existingSelection: string | null | undefined
): DDProps[] {
    const options: DDProps[] = [];
    let existingFound = false;

    if (vehicleResponse) {
        for (const vehicle of vehicleResponse.vehicles) {
            if (vehicle.identifier === existingSelection) existingFound = true;

            options.push({
                value: vehicle.identifier,
                label: vehicle.identifier
            });
        }
    }

    options.sort((a, b) => a.label.localeCompare(b.label));

    if (!existingFound && existingSelection) {
        options.unshift({
            value: existingSelection,
            label: existingSelection
        });
    }

    return options;
}
